import { createFileRoute, redirect, useLocation, useNavigate, useSearch } from "@tanstack/react-router";
import { AdministratorProfilesTable } from "./-components/administrator-profiles-table";
import { useQuery } from "@tanstack/react-query";
import { useContext, useEffect, useMemo } from "react";
import { DashboardContext } from "@/components/dashboard-layout";
import { organisationsQueryOptions } from "@/server/organisations";
import { queuesQueryOptions } from "@/server/queues";
import { AdministratorProfileDetails } from "./-components/administrator-profiles-sidebar";
import { AdminProfileReadable, CrudEntry, OrganisationReadable, QueueReadable } from "@/lib/mma/types.gen";
import { administratorAccountsQueryOptions, administratorProfilesQueryOptions } from "@/server/administrators";
import { toast } from "sonner";
import { useRightSidebarClose } from "@/hooks/use-right-sidebar-close";
import { useRightSidebarOpen } from "@/hooks/use-right-sidebar-open";
import {
  checkIfUserMayRead,
  checkIfUserMayWrite,
  checkIfUserMayDelete,
  ContextUser,
  getNextRedirectRoute,
} from "@/lib/utils";

export const Route = createFileRoute("/_dashboard/administrators/profiles")({
  component: RouteComponent,
  beforeLoad: ({ context }) => {
    const user = context.user as ContextUser;
    const mayRead = checkIfUserMayRead(user?.profile, user?.accessControl, "ADMIN_PROFILES");

    if (!mayRead) {
      const nextRedirectRoute = getNextRedirectRoute(user);
      throw redirect({ to: nextRedirectRoute });
    }
  },
  loader: ({ context }) => {
    return {
      crumb: "Administrator profiles",
      userProfileId: (context as { user?: { profile: string } }).user?.profile,
      accessControl: (context as { user?: { accessControl: Record<string, CrudEntry> } }).user?.accessControl,
    };
  },
});

function RouteComponent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfileId, accessControl } = Route.useLoaderData();
  //If the user has no profile, it means they have access to everything. Profiles are restrictive only.
  const mayWrite = checkIfUserMayWrite(userProfileId, accessControl, "ADMIN_PROFILES");
  const mayDelete = checkIfUserMayDelete(userProfileId, accessControl, "ADMIN_PROFILES");
  const searchParams = useSearch({ strict: false }) as {
    search?: string;
    organisation?: string;
    queue?: string;
    page?: string;
    perPage?: string;
    sortBy?: string;
    sortDir?: "asc" | "desc";
    id?: string;
  };
  const context = useContext(DashboardContext);
  const closeRightSidebar = useRightSidebarClose();
  const openRightSidebar = useRightSidebarOpen();

  const profilesQuery = useQuery(administratorProfilesQueryOptions);
  const profiles = profilesQuery.data as AdminProfileReadable[];
  const organisationsQuery = useQuery(organisationsQueryOptions());
  const queuesQuery = useQuery(queuesQueryOptions);

  // Memoise fallback arrays so they remain referentially stable when there's no data (e.g. on 500 errors)
  const organisations = useMemo(() => organisationsQuery.data ?? [], [organisationsQuery.data]);
  const queues = useMemo(() => queuesQuery.data ?? [], [queuesQuery.data]);
  const accountsQuery = useQuery(administratorAccountsQueryOptions);
  const accounts = accountsQuery.data;

  const modifiedProfiles =
    profiles?.map((profile) => {
      let profileUsers = 0;
      accounts?.forEach((account) => {
        if (account.profileId === profile.id) {
          profileUsers++;
        }
      });
      return {
        ...profile,
        profileUsers,
        accessControlMatrix: profile.accessControlMatrix //Remove this when mock is removed!!!
          ? Object.fromEntries(
              Object.entries(profile.accessControlMatrix).map(([key, value]) => [
                key,
                value as Array<"C" | "R" | "U" | "D">,
              ])
            )
          : {},
      };
    }) || [];

  const isExact = location.href === "/administrators/profiles" || location.href === "/administrators/profiles/";

  // Build sidebar component based on searchParams
  const sidebarComponent = useMemo(() => {
    if (searchParams.id === undefined) return null;
    return <AdministratorProfileDetails profileId={searchParams.id === "new" ? null : searchParams.id} />;
  }, [searchParams.id]);

  // Handle sidebar opening/closing based on search params
  useEffect(() => {
    if (!context) {
      return;
    }

    const shouldClose = isExact && !!context.rightSidebar;
    const hasPermission = searchParams.id !== "new" || mayWrite;

    openRightSidebar({
      component: sidebarComponent,
      paramKeys: "id",
      shouldClose,
      permissionCheck: !hasPermission,
      onPermissionDenied: () => closeRightSidebar("id"),
    });
  }, [searchParams.id, sidebarComponent, mayWrite, isExact, openRightSidebar, closeRightSidebar]);

  useEffect(() => {
    if (organisationsQuery.isError) {
      const message =
        organisationsQuery.error instanceof Error
          ? organisationsQuery.error.message
          : "Failed to load organisations. Please try again later.";
      toast.error(message);
    }
  }, [organisationsQuery.isError, organisationsQuery.error]);

  useEffect(() => {
    if (profilesQuery.isError) {
      const message =
        profilesQuery.error instanceof Error
          ? profilesQuery.error.message
          : "Failed to load profiles. Please try again later.";
      toast.error(message);
    }
  }, [profilesQuery.isError, profilesQuery.error]);

  useEffect(() => {
    if (queuesQuery.isError) {
      const message =
        queuesQuery.error instanceof Error
          ? queuesQuery.error.message
          : "Failed to load queues. Please try again later.";
      toast.error(message);
    }
  }, [queuesQuery.isError, queuesQuery.error]);

  useEffect(() => {
    if (accountsQuery.isError) {
      const message =
        accountsQuery.error instanceof Error
          ? accountsQuery.error.message
          : "Failed to load accounts. Please try again later.";
      toast.error(message);
    }
  }, [accountsQuery.isError, accountsQuery.error]);

  const handleAddProfile = () => {
    navigate({
      to: "/administrators/profiles",
      replace: true,
      search: (prev) => ({ ...prev, id: "new" }),
    });
  };

  return (
    <AdministratorProfilesTable
      profiles={modifiedProfiles as AdminProfileReadable[]}
      organisations={organisations}
      queues={queues}
      handleAddProfile={handleAddProfile}
      isLoading={organisationsQuery.isLoading || queuesQuery.isLoading}
      mayWrite={mayWrite}
      mayDelete={mayDelete}
    />
  );
}
